import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ticketService, useInboxFolders, type InboxFolder } from '@socialflows/shared-lib';
import type { FolderCounts } from '@/hooks/use-folder-counts';

interface InboxContextType {
  folderCounts: FolderCounts;
  customFolders: InboxFolder[];
  loadingCounts: boolean;
  loadingFolders: boolean;
  refreshCounts: () => void;
  refreshFolders: () => void;
  incrementFolderCounts: (affectedFolders: string[]) => void;
}

const InboxContext = createContext<InboxContextType | null>(null);

export function InboxProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const foldersQuery = useInboxFolders();

  const countsQuery = useQuery({
    queryKey: ['ticket-counts'],
    queryFn: () => ticketService.getFolderCounts(),
  });

  const [folderCounts, setFolderCounts] = useState<FolderCounts>({
    urgent: 0,
    vip: 0,
    buyers: 0,
    all: 0,
    reminders: 0,
    myTickets: 0,
    backlog: 0,
    spam: 0,
    archived: 0,
    customFolders: {},
  });

  useEffect(() => {
    if (countsQuery.data) {
      setFolderCounts({
        all: countsQuery.data.all || 0,
        urgent: countsQuery.data.urgent || 0,
        vip: countsQuery.data.vip || 0,
        buyers: countsQuery.data.buyers || 0,
        myTickets: countsQuery.data.myTickets || 0,
        backlog: countsQuery.data.backlog || 0,
        spam: countsQuery.data.spam || 0,
        archived: countsQuery.data.archived || 0,
        reminders: countsQuery.data.reminders || 0,
        customFolders: countsQuery.data.customFolders || {},
      });
    }
  }, [countsQuery.data]);

  const incrementFolderCounts = useCallback((affectedFolders: string[]) => {
    setFolderCounts((prev) => {
      const next = { ...prev, customFolders: { ...prev.customFolders } };
      for (const folder of affectedFolders) {
        switch (folder) {
          case 'all':
            next.all = Math.max(0, next.all + 1);
            break;
          case 'urgent':
            next.urgent = Math.max(0, next.urgent + 1);
            break;
          case 'vip':
            next.vip = Math.max(0, next.vip + 1);
            break;
          case 'buyers':
            next.buyers = Math.max(0, next.buyers + 1);
            break;
          case 'myTickets':
            next.myTickets = Math.max(0, next.myTickets + 1);
            break;
          default:
            if (folder.startsWith('custom:')) {
              const folderId = folder.replace('custom:', '');
              next.customFolders[folderId] = Math.max(0, (next.customFolders[folderId] || 0) + 1);
            }
            break;
        }
      }
      return next;
    });
  }, []);

  const customFolders = foldersQuery.data ?? [];
  const loadingCounts = countsQuery.isLoading;
  const loadingFolders = foldersQuery.isLoading;

  const refreshCounts = useCallback(() => {
    queryClient.refetchQueries({ queryKey: ['ticket-counts'] });
  }, [queryClient]);

  const refreshFolders = useCallback(() => {
    foldersQuery.refetch();
  }, [foldersQuery]);

  useEffect(() => {
    function handleFocus() {
      queryClient.refetchQueries({ queryKey: ['ticket-counts'] });
    }
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [queryClient]);

  return (
    <InboxContext.Provider
      value={{
        folderCounts,
        customFolders,
        loadingCounts,
        loadingFolders,
        refreshCounts,
        refreshFolders,
        incrementFolderCounts,
      }}
    >
      {children}
    </InboxContext.Provider>
  );
}

export function useInbox() {
  const ctx = useContext(InboxContext);
  if (!ctx) throw new Error('useInbox must be used within InboxProvider');
  return ctx;
}
